import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import { TEMPO_DE_POLL_MS, telaVisivel } from '../helpers/rede';

// Barra de abas no rodapé — só aparece no dedo (celular/tablet), por CSS.
//
// Ela SUBSTITUI o hambúrguer: no toque, o mapa completo do sistema mora em
// "Mais", e não em dois lugares. Duas portas pra mesma coisa foi justamente o
// que incomodou no app.
//
// A ordem é por alcance do polegar direito, não por importância no organograma:
// o que se usa o dia inteiro fica na direita, perto do dedo. Por isso Conversas
// encosta em "Mais" e Contatos fica na ponta esquerda.
//
// Equipe não é aba: ela vive DENTRO de Grupos, destacada no topo da lista. São
// a mesma natureza de conversa (várias pessoas, fora da fila do cliente), e uma
// aba só pra isso gastava o melhor espaço da barra.
const ABAS = [
  {
    chave: 'contatos',
    rotulo: 'Contatos',
    icone: 'bi-person-circle',
    para: '/ti/contatos',
    permissao: 'ti:atendimentos',
  },
  {
    chave: 'grupos',
    rotulo: 'Grupos',
    icone: 'bi-people',
    para: '/ti/grupos',
    permissao: 'ti:atendimentos',
  },
  {
    chave: 'conversas',
    rotulo: 'Conversas',
    icone: 'bi-chat-dots',
    para: '/ti/atendimentos',
    permissao: 'ti:atendimentos',
  },
  {
    chave: 'mais',
    rotulo: 'Mais',
    icone: 'bi-three-dots',
    para: '/ti/mais',
  },
];

export default function BarraAbas() {
  const { temPermissao } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Não-lidas na própria aba. Sem isso, quem está em Contatos ou em Mais não
  // tem como saber que entrou conversa — a barra é a única coisa na tela o
  // tempo todo, e era a única que não avisava.
  const podeAtender = temPermissao('ti:atendimentos');
  const [naoLidas, setNaoLidas] = useState({ conversas: 0, grupos: 0 });

  // A barra só existe no dedo — quem esconde ela é o CSS, mas o JavaScript não
  // sabia disso e ficava consultando duas rotas a cada 15s em toda tela de
  // computador, pra pintar uma bolinha que ninguém vê.
  //
  // A medida é a MESMA do CSS, copiada letra por letra (procure `.barra-abas`
  // no index.css). Se as duas divergirem, aparece o pior caso possível: a barra
  // à mostra e o contador congelado em zero.
  const MEDIDA_DEDO = '(max-width: 860px), (pointer: coarse) and (max-width: 1400px)';
  const [noDedo, setNoDedo] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MEDIDA_DEDO).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MEDIDA_DEDO);
    const ao = (e) => setNoDedo(e.matches);
    mq.addEventListener('change', ao);
    return () => mq.removeEventListener('change', ao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!podeAtender || !noDedo) return undefined;
    let vivo = true;
    let emVoo = false; // ver helpers/rede: não empilha, não roda com a tela escondida
    const buscar = async () => {
      if (emVoo || !telaVisivel()) return;
      emVoo = true;
      // Falha aqui não vira erro na tela: contador é acessório, e a barra
      // precisa continuar navegando mesmo com a rede oscilando.
      const [c, g] = await Promise.all([
        axios.get(`${API}/atendimentos/nao-lidas/total`, { timeout: TEMPO_DE_POLL_MS }).catch(() => null),
        axios.get(`${API}/grupos/nao-lidas`, { timeout: TEMPO_DE_POLL_MS }).catch(() => null),
      ]);
      emVoo = false;
      if (!vivo) return;
      setNaoLidas({
        conversas: Number(c?.data?.data?.total || 0),
        grupos: Number(g?.data?.data?.total || 0),
      });
    };
    buscar();
    const id = setInterval(buscar, 15000);
    return () => { vivo = false; clearInterval(id); };
  }, [podeAtender, noDedo]);

  const visiveis = ABAS.filter((a) => !a.permissao || temPermissao(a.permissao));
  if (visiveis.length === 0) return null;

  const ativa = (aba) => {
    // Conversas é comparação exata: `startsWith` acenderia ela também em
    // /ti/atendimentos/indicadores.
    if (aba.chave === 'conversas') return pathname === '/ti/atendimentos';
    return pathname.startsWith(aba.para);
  };

  return (
    <nav className="barra-abas" aria-label="Navegação principal">
      {visiveis.map((aba) => (
        <button
          key={aba.chave}
          type="button"
          className={`barra-abas__item ${ativa(aba) ? 'is-ativa' : ''}`}
          onClick={() => navigate(aba.para)}
          aria-current={ativa(aba) ? 'page' : undefined}
        >
          <span className="barra-abas__icone">
            <i className={`bi ${aba.icone}`} />
            {naoLidas[aba.chave] > 0 && (
              <span className="barra-abas__badge">
                {naoLidas[aba.chave] > 99 ? '99+' : naoLidas[aba.chave]}
              </span>
            )}
          </span>
          <span>{aba.rotulo}</span>
        </button>
      ))}
    </nav>
  );
}
